/**
 * Bottleneck Detection Alerts (MA-IMPL-120 - Phase 5)
 * 
 * Displays rule-based alerts for performance bottlenecks.
 * Surfaces issues like low conversion rates, high CPL, low deployable rate.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  AlertCircle,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  Target,
  Users,
  CheckCircle,
  Activity,
  Shield,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { api } from '../../lib/api';

// =============================================================================
// API HOOK
// =============================================================================

function useBottleneckDetection({ period, workspaceId, tenantId }) {
  return useQuery({
    queryKey: ['bottleneck-detection', period, workspaceId, tenantId],
    queryFn: async () => {
      const params = new URLSearchParams({ period });
      if (workspaceId) params.append('workspace_id', workspaceId);
      if (tenantId) params.append('tenant_id', tenantId);
      
      const res = await api.get(`/analytics/bottleneck-detection?${params}`);
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
}

// =============================================================================
// ALERT CARD COMPONENT
// =============================================================================

function AlertCard({ alert }) {
  const isCritical = alert.severity === 'critical';
  
  const categoryIcons = {
    conversion: TrendingDown,
    economic: Target,
    compliance: Shield,
  };
  
  const CategoryIcon = categoryIcons[alert.category] || AlertTriangle;
  
  return (
    <div 
      className={`rounded-lg border p-4 ${
        isCritical 
          ? 'bg-red-500/10 border-red-500/30' 
          : 'bg-amber-500/10 border-amber-500/30'
      }`}
      data-testid={`bottleneck-alert-${alert.metric}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${isCritical ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
          <CategoryIcon className={`w-4 h-4 ${isCritical ? 'text-red-400' : 'text-amber-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge 
              variant="outline" 
              className={isCritical ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}
            >
              {isCritical ? 'Critical' : 'Warning'}
            </Badge>
            <Badge variant="outline" className="bg-slate-800 text-slate-300 border-slate-700 text-xs">
              {alert.entity_type}: {alert.entity_name}
            </Badge>
          </div>
          <p className={`text-sm font-medium ${isCritical ? 'text-red-300' : 'text-amber-300'}`}>
            {alert.message}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
            <span>Metric: <span className="text-slate-300">{alert.metric.replace(/_/g, ' ')}</span></span>
            <span>Value: <span className={isCritical ? 'text-red-400' : 'text-amber-400'}>{alert.value}%</span></span>
            <span>Threshold: <span className="text-slate-300">{alert.threshold}%</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// SUMMARY BADGE COMPONENT
// =============================================================================

function StatusBadge({ criticalCount, warningCount }) {
  if (criticalCount > 0) {
    return (
      <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
        <AlertCircle className="w-3.5 h-3.5 mr-1" />
        {criticalCount} Critical
      </Badge>
    );
  }
  if (warningCount > 0) {
    return (
      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
        <AlertTriangle className="w-3.5 h-3.5 mr-1" />
        {warningCount} Warning{warningCount > 1 ? 's' : ''}
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
      <CheckCircle className="w-3.5 h-3.5 mr-1" />
      All Clear
    </Badge>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function BottleneckAlerts({ 
  period = 'mtd', 
  workspaceId, 
  tenantId,
  compact = false 
}) {
  const [isOpen, setIsOpen] = useState(true);
  const { data, isLoading, error, refetch, isFetching } = useBottleneckDetection({
    period,
    workspaceId,
    tenantId,
  });

  const alerts = data?.alerts || [];
  const criticalCount = data?.critical_count || 0;
  const warningCount = data?.warning_count || 0;
  const totalAlerts = data?.total_alerts || 0;

  // Group alerts by category
  const conversionAlerts = alerts.filter(a => a.category === 'conversion');
  const economicAlerts = alerts.filter(a => a.category === 'economic');
  const complianceAlerts = alerts.filter(a => a.category === 'compliance');

  // Compact view - just shows badge
  if (compact) {
    return (
      <div className="flex items-center gap-2" data-testid="bottleneck-alerts-compact">
        <StatusBadge criticalCount={criticalCount} warningCount={warningCount} />
        {totalAlerts > 0 && (
          <span className="text-xs text-slate-400">
            {totalAlerts} issue{totalAlerts !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    );
  }

  if (error) {
    // MA-PHASE11A-WARROOM-FINAL-VALIDATION-021: Neutral error state
    return (
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium text-sm">Bottleneck Detection Unavailable</p>
          <p className="text-slate-500 text-xs mt-1">Unable to load bottleneck analysis at this time.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="bottleneck-alerts">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer group">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  criticalCount > 0 ? 'bg-red-500/10' : 
                  warningCount > 0 ? 'bg-amber-500/10' : 
                  'bg-emerald-500/10'
                }`}>
                  <Activity className={`w-5 h-5 ${
                    criticalCount > 0 ? 'text-red-400' : 
                    warningCount > 0 ? 'text-amber-400' : 
                    'text-emerald-400'
                  }`} />
                </div>
                <div>
                  <CardTitle className="text-base text-white flex items-center gap-2">
                    Bottleneck Detection
                    <StatusBadge criticalCount={criticalCount} warningCount={warningCount} />
                  </CardTitle>
                  <CardDescription className="text-xs text-slate-400">
                    Rule-based performance alerts
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={(e) => { e.stopPropagation(); refetch(); }}
                  disabled={isFetching}
                  className="h-8 w-8 p-0"
                >
                  <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
                </Button>
                {isOpen ? (
                  <ChevronUp className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                )}
              </div>
            </div>
          </CollapsibleTrigger>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {isLoading ? (
              <div className="py-8 text-center text-slate-400">
                <div className="animate-pulse">Analyzing bottlenecks...</div>
              </div>
            ) : alerts.length === 0 ? (
              <div className="py-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h4 className="text-lg font-medium text-emerald-400 mb-1">No Bottlenecks Detected</h4>
                <p className="text-slate-500 text-sm max-w-md mx-auto">
                  All campaigns are performing within acceptable thresholds. 
                  Keep monitoring for emerging issues.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Conversion Bottlenecks */}
                {conversionAlerts.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                      <TrendingDown className="w-3.5 h-3.5" />
                      Conversion Issues ({conversionAlerts.length})
                    </h5>
                    <div className="space-y-2">
                      {conversionAlerts.map((alert, idx) => (
                        <AlertCard key={`${alert.entity_id}-${alert.metric}-${idx}`} alert={alert} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Economic Bottlenecks */}
                {economicAlerts.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                      <Target className="w-3.5 h-3.5" />
                      Economic Issues ({economicAlerts.length})
                    </h5>
                    <div className="space-y-2">
                      {economicAlerts.map((alert, idx) => (
                        <AlertCard key={`${alert.entity_id}-${alert.metric}-${idx}`} alert={alert} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Compliance Bottlenecks */}
                {complianceAlerts.length > 0 && (
                  <div>
                    <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                      <Shield className="w-3.5 h-3.5" />
                      Compliance Issues ({complianceAlerts.length})
                    </h5>
                    <div className="space-y-2">
                      {complianceAlerts.map((alert, idx) => (
                        <AlertCard key={`${alert.entity_id}-${alert.metric}-${idx}`} alert={alert} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
